import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import ProgressBar from './ProgressBar';
import { withUnit } from '../../utils/maxesGoalsHelpers';

const Card = styled.div`
  flex: 1;
  min-width: 460px;
  min-height: 220px;
  max-height: 340px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GoalRowsScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const NoGoalsState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 24px 16px;
`;

const NoGoalsIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const GoalList = styled.div`
  display: grid;
  grid-template-columns: fit-content(280px) fit-content(280px) auto 1fr;
  column-gap: 24px;
`;

const GoalRow = styled.div`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #F5F5F5;

  &:last-of-type {
    border-bottom: none;
  }
`;

const GoalInfo = styled.div`
  display: contents;
`;

const GoalNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

// Clicking a goal picks its exercise in the filter
const GoalName = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: #1A1A1A;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.hover};
  }
`;

const GoalValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const GoalValue = styled.span`
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GoalProgress = styled.div`
  grid-column: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 260px;
  flex-shrink: 0;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;
`;

const GoalActions = styled.div`
  grid-column: 4;
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFFFFF;
  padding: 6px 12px;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  color: #1A1A1A;
  cursor: pointer;

  &:hover {
    background: #F7F7F7;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

export const formatGoalValue = (goal) => {
  if (goal.linkedExercise) return `${goal.linkedExercise.percentage}% ${goal.linkedExercise.name}`;
  return withUnit(goal.value, goal.unitAbbr);
};

// Only shown for one athlete so the coach can compare it to the goal
const formatCurrentValue = (goal) => {
  const currentValue = withUnit(goal.currentValue, goal.currentUnitAbbr);
  return currentValue && `Current: ${currentValue}`;
};

const GoalsCard = ({
  title, subtitle, goals, isLoading, emptyMessage,
  onAddGoal, onEditGoal, showHistory, onViewHistory, onSelectExercise,
}) => (
  <Card>
    <CardHeader>
      <TitleBlock>
        <Text styledTextProps={{ fontSize: '20px', fontWeight: 700, color: '#D64545' }}>
          {title}
        </Text>
        {subtitle && (
          <Text styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}>{subtitle}</Text>
        )}
      </TitleBlock>
      {!emptyMessage && onAddGoal && (
        <LinkButton type='button' onClick={onAddGoal}>Add a goal</LinkButton>
      )}
    </CardHeader>
    {emptyMessage && (
      <Text styledWrapperProps={{ py: '8px' }} styledTextProps={{ fontSize: '14px', color: '#1A1A1A' }}>
        {emptyMessage}
      </Text>
    )}
    {!emptyMessage && (
      <GoalRowsScrollArea>
        {isLoading && <LoadingWrapper><Spinner large={false} /></LoadingWrapper>}
        {!isLoading && goals.length === 0 && (
          <NoGoalsState>
            <NoGoalsIcon>
              <Icon size={32} icon='ribbon' color='#9E9E9E' />
            </NoGoalsIcon>
            <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
              {onAddGoal ? 'Click \u2018Add a goal\u2019 above to get started.' : 'No goals set yet.'}
            </Text>
          </NoGoalsState>
        )}
        {!isLoading && goals.length > 0 && (
          <GoalList>
            {goals.map((goal) => (
              <GoalRow key={goal.goalId}>
                <GoalInfo>
                  <GoalNameBlock>
                    <GoalName
                      type='button'
                      title={`Show ${goal.exerciseName} maxes`}
                      onClick={() => onSelectExercise(goal)}
                    >
                      {goal.exerciseName}
                    </GoalName>
                    {goal.sourceLabel && (
                    <Text
                      upperCase
                      styledTextProps={{
                        fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', color: '#9E9E9E',
                      }}
                    >
                      {`From ${goal.sourceLabel}`}
                    </Text>
                    )}
                  </GoalNameBlock>
                  <GoalValueBlock>
                    <GoalValue title={formatGoalValue(goal)}>{formatGoalValue(goal)}</GoalValue>
                    {formatCurrentValue(goal) && (
                    <Text styledTextProps={{ fontSize: '11px', color: '#9E9E9E' }}>
                      {formatCurrentValue(goal)}
                    </Text>
                    )}
                  </GoalValueBlock>
                  {/* No progress means it's not shown here - 0 means not started */}
                  {goal.progressPct !== undefined && goal.progressPct !== null && (
                  <GoalProgress>
                    {goal.progressPct > 0 ? (
                      <>
                        <ProgressBar percent={goal.progressPct} color={goal.progressColor} height='10px' />
                        <span>{`${goal.progressPct}%`}</span>
                      </>
                    ) : (
                      <Text styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}>Not started</Text>
                    )}
                  </GoalProgress>
                  )}
                </GoalInfo>
                <GoalActions>
                  {onEditGoal && (
                  <EditButton type='button' onClick={() => onEditGoal(goal)}>
                    <Icon icon='pencil' size={12} />
                    Edit
                  </EditButton>
                  )}
                  {showHistory && (
                  <EditButton type='button' onClick={() => onViewHistory(goal)}>
                    <Icon icon='chart-line' size={12} />
                    History
                  </EditButton>
                  )}
                </GoalActions>
              </GoalRow>
            ))}
          </GoalList>
        )}
      </GoalRowsScrollArea>
    )}
  </Card>
);

GoalsCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  goals: PropTypes.arrayOf(PropTypes.shape({
    goalId: PropTypes.number,
    exerciseName: PropTypes.string,
    value: PropTypes.string,
    unitAbbr: PropTypes.string,
    linkedExercise: PropTypes.shape({
      name: PropTypes.string,
      percentage: PropTypes.number,
    }),
    sourceLabel: PropTypes.string,
    progressPct: PropTypes.number,
    progressColor: PropTypes.string,
    currentValue: PropTypes.string,
    currentUnitAbbr: PropTypes.string,
  })),
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onAddGoal: PropTypes.func,
  onEditGoal: PropTypes.func,
  showHistory: PropTypes.bool,
  onViewHistory: PropTypes.func,
  onSelectExercise: PropTypes.func,
};

GoalsCard.defaultProps = {
  title: 'Goals',
  subtitle: null,
  goals: [],
  isLoading: false,
  emptyMessage: null,
  onAddGoal: null,
  onEditGoal: null,
  showHistory: false,
  onViewHistory: () => {},
  onSelectExercise: () => {},
};

export default GoalsCard;
